/**
 * Session-scoped impression deduplication.
 *
 * Components that fire "viewed" / "rendered" impression events typically
 * guard with a per-instance `useRef` so a single mount fires once. That
 * guard is reset whenever the component unmounts and remounts — which is
 * exactly what virtualized lists (the chat message list) do as rows
 * scroll out of and back into the viewport. The result is the same
 * message re-firing its impression every time it is recycled, inflating
 * impression counts and understating every funnel rate that uses an
 * impression as its denominator.
 *
 * This module holds a process-lifetime set of keys so an impression for
 * a given key fires at most once per app session, no matter how many
 * times its component remounts. The set is intentionally never pruned —
 * it resets naturally when the JS context is torn down (app restart),
 * which matches the "unique impressions per session" semantics analytics
 * dashboards expect.
 *
 * Keys should be namespaced by event so distinct impression events on the
 * same entity don't suppress one another, e.g.
 * `add-references-prompt:${messageSid}`.
 */
const firedImpressionKeys = new Set<string>();

/**
 * Returns `true` the first time `key` is seen in this session and marks
 * it as fired; returns `false` on every subsequent call for the same key.
 * Call once at the point you would otherwise fire the impression event.
 */
export function shouldFireImpression(key: string): boolean {
  if (firedImpressionKeys.has(key)) {
    return false;
  }

  firedImpressionKeys.add(key);

  return true;
}

/**
 * Clears the recorded keys. Test-only — production code relies on the set
 * persisting for the whole session. Call from `afterEach` so impression
 * state does not leak across test cases.
 */
export function __resetImpressionDedupForTests(): void {
  firedImpressionKeys.clear();
}
